import { select } from '@ptl/angular-redux-store';
import { Component, Input, OnInit } from '@angular/core';
import { ContentStorageService } from '@ptl/global-components';
import { Observable } from 'rxjs';
import { BenefitsByPlanCollection, BenefitValue, MedicalBenefit, PlanDetailsRenderableGroup, PlanMeta } from '../../../../types/plan-details.types';

@Component({
    selector: 'app-medical-benefit',
    templateUrl: './medical-benefit.component.html',
    styleUrls: ['./medical-benefit.component.scss'],
    standalone: false
})
export class MedicalBenefitComponent implements OnInit {

  @Input() benefitGroup: PlanDetailsRenderableGroup;
  content: Map<any, any>;

  sortedPlanNames: PlanMeta[];

  /** Maps whether plan/benefit/network combination should include a highlight based on differences during compare plans */
  shouldHighlight = new Map();
  @select(['componentStates', 'ComparePlansHighlight', 'showHighlights'])
  showHighlights$: Observable<boolean>;

  constructor(
    private contentStorage: ContentStorageService
  ) { }

  ngOnInit(): void {

    this.sortedPlanNames = this.benefitGroup.plans.sort((a, b) => {
      if (a.id < b.id) { return -1; }
      if (a.id > b.id) { return 1; }
      return 0;
    });

    this.content = this.contentStorage.getContentMap('planDetailsContent');

    this.getBenefitDifferences();
  }

  trackByIndex(index: number, nonSpecificObj: any): number {
    return index;
  }

  getValueOrFiller = (tierVal: string): string | 'Not Covered' => {
    if (!tierVal || tierVal === 'N/A') { 
      return 'Not Covered'; 
    }
    return tierVal;
  }


  /** Table Structure Functions */
  doesBenefitLimitExistForPlan = (planId, benefitId): boolean => {
    return (this.benefitGroup.planBenefits[planId][benefitId] as MedicalBenefit).Limit ? true : false;
  }

  mustIncludeLimitRow = (benefitId) => {
    const planIds = Object.keys(this.benefitGroup.planBenefits);
    for (let planId of planIds) {
      const benefitData: MedicalBenefit = this.benefitGroup.planBenefits[planId][benefitId] as MedicalBenefit;
      if (benefitData.Limit) {
        return true;
      }
    }
  } 

  getLimitColSpan = (benefitId) => {
    const planIds = Object.keys(this.benefitGroup.planBenefits);
    let disqualifiers = {
      limitMissing: false,
      uniqueLimitFound: false
    };
    let activeLimit = null;

    for (let planId of planIds) {
      const benefitData: MedicalBenefit = this.benefitGroup.planBenefits[planId][benefitId] as MedicalBenefit;
      const limitForPlanBenefit = benefitData.Limit;
      if (!limitForPlanBenefit) {
        /** Benefit on Plan doesn't have a limit */
        disqualifiers.limitMissing = true;
        break;
      } else if (!activeLimit) {
        /** Store to compare against */
        activeLimit = limitForPlanBenefit;
      } else if (activeLimit !== limitForPlanBenefit) {
        /** Limit compare yields difference */
        disqualifiers.uniqueLimitFound = true;
        break;
      }
    }

    let limitShouldMerge = true;
    for (let key in disqualifiers) {
      if (disqualifiers[key] === true) { limitShouldMerge = false; }
    }

    if (limitShouldMerge) {
      return planIds.length.toString();
    } else {
      return '1';
    }

  }

  getNetworksToDisplay(benefitId) { 
 
    const uniqNetworks = new Set();

    Object.keys(this.benefitGroup.planBenefits).forEach(planId => {
      const planInfo = this.benefitGroup.planBenefits[planId];
      const benefitOnPlan: MedicalBenefit = planInfo[benefitId] as MedicalBenefit;
      for (let benefit in benefitOnPlan) {
        if (benefit.toLowerCase() !== "limit") {
          uniqNetworks.add(benefit);
        }
      }
    })
    
    const returnable = Object.assign({}, Array.from(uniqNetworks));
    return returnable;
  }

  findModeOfValues(arr: any[]): {
    allSame: boolean,
    allDifferent: boolean,
    modeValue: string
  } {

    const returnable = {
      allSame: false,
      allDifferent: false,
      modeValue: null
    }

    /** Create MAP to store NUMBER OF MATCHES per array item */
    const resultsMap = new Map();
    // UNIQUE the array, and DO MATCHING, and MAP POPULATION
    Array.from(new Set(JSON.parse(JSON.stringify(arr)))).forEach((v, i, a) => {
      if (!resultsMap.get(v)) {
        resultsMap.set(v.toString(), arr.filter(val => val === v).length)
      }
    });

    /** Create OBJECT from MAP */
    const resultsMapObj = {};
    const entryIterator = resultsMap.keys();
    let entry = entryIterator.next();
    while (!entry.done) {
      resultsMapObj[entry.value] = resultsMap.get(entry.value);
      entry = entryIterator.next();
    }

    /** Create ARRAY from OBJECT */
    let resultsMapArray = Object.keys(resultsMapObj).map(k => {
      return { 'key': k, 'value': resultsMap.get(k) }
    });

    /** SORT ARRAY */
    resultsMapArray.sort((a, b) => b.value - a.value);

    // ALL THE SAME
    if (Object.keys(resultsMapArray).length === 1) {
      returnable.allSame = true;
    }

    // NO MODE - all different
    else if (
      Object.keys(resultsMapArray).length > 1 &&
      new Set(
        resultsMapArray
          .map(a => a.key === resultsMapArray[0].key)
      ).size !== resultsMapArray.length
    ) {
      returnable.allDifferent = true;
    }

    // HAS MODE
    else {
      returnable.modeValue = resultsMapArray[0].key;
    }

    /** Return MODE - @TODO does not capture circumstance of multiple modes [ should not be an issue w/ only max-3 item ] */
    return returnable;
  }

  getBenefitDifferences() {

    const planBenefits = this.benefitGroup.planBenefits;

    /** Loop through PLANS */
    const planKeys = Object.keys(planBenefits);
    const planKeysMap: Map<string, Map<string, Map<string, string | boolean>>> = new Map();

    for (let planKey of planKeys) {

      const benefitsInPlan = this.benefitGroup.planBenefits[planKey];

      /** Loop through BENEFITS */
      const benefitKeys = Object.keys(benefitsInPlan);
      const benefitKeysMap = new Map();

      for (let benefitKey of benefitKeys) {

        const networksInBenefit = this.benefitGroup.planBenefits[planKey][benefitKey];

        /** Loop through NETWORK */
        const networkKeys = Object.keys(networksInBenefit);
        const networkKeysMap = new Map();

        for (let networkKey of networkKeys) {
          let valueOfNetworkBenefit = networksInBenefit[networkKey];
          networkKeysMap.set(networkKey, valueOfNetworkBenefit); // <-- this will be true or false, based on whether or not we will highlight this value
        }

        benefitKeysMap.set(benefitKey, networkKeysMap);

      }

      planKeysMap.set(planKey, benefitKeysMap);

    }

    // GENERATE ARRAY TO IDENTIFY MODE
    // - use 1 plan to prime benefits iterations
    // - loop through benefits, then plans, then networks
    // - for each store reference to the value sinside of an active benefit array
    // - at the end, find the mode of the array ( this identifies the non-highlighted, primary value )
    // - set all non-matching mapped values to true ( this indicates that they will be highlighted )

    const planIterator = planKeysMap.keys();
    let planEntry = planIterator.next(); // prime the iterator - only do 1


    /** Using the BENEFITS MAP - iterate */
    const benefitKeysMap: Map<string, Map<string, string | boolean>> = planKeysMap.get(planEntry.value);
    const benefitIterator = benefitKeysMap.keys();
    let benefitEntry = benefitIterator.next(); // prime the iterator
    while (!benefitEntry.done) {

      /** Using the NETWORKS MAP - iterate */
      const networkKeysMap: Map<string, string | boolean> = benefitKeysMap.get(benefitEntry.value);
      const networkIterator = networkKeysMap.keys();
      let networkEntry = networkIterator.next(); // prime the iterator
      while (!networkEntry.done) {

        const benefitsValueArray = [];

        /** Using the PLANS MAP - iterate */
        let plan2Iterator = planKeysMap.keys();
        let plan2Entry = plan2Iterator.next(); // prime the iterator

        while (!plan2Entry.done) {

          const planKeysMapVals = planKeysMap
            .get(plan2Entry.value)
            .get(benefitEntry.value)
            .get(networkEntry.value)

          if (planKeysMapVals) {
            // Add values to array to generate mode
            benefitsValueArray.push(
              planKeysMapVals
            );
          }

          plan2Entry = plan2Iterator.next();
        }

        // Generate mode
        const modeOfBenefitNetworkValues = this.findModeOfValues(benefitsValueArray);

        /** Using the PLANS MAP - iterate(again) */
        plan2Iterator = planKeysMap.keys();
        plan2Entry = plan2Iterator.next(); // prime the iterator

        while (!plan2Entry.done) {

          const curVal = planKeysMap.get(plan2Entry.value).get(benefitEntry.value).get(networkEntry.value);

          const shouldHighlight =
            modeOfBenefitNetworkValues.allDifferent ||
            (!modeOfBenefitNetworkValues.allSame && curVal !== modeOfBenefitNetworkValues.modeValue);

          // Set value
          planKeysMap
            .get(plan2Entry.value)
            .get(benefitEntry.value)
            .set(networkEntry.value, shouldHighlight);

          plan2Entry = plan2Iterator.next();
        }

        networkEntry = networkIterator.next();
      }


      benefitEntry = benefitIterator.next();
    }

    this.shouldHighlight = planKeysMap;
    // console.log(this.shouldHighlight);

  }
}
